import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { FilterValues } from "../../../data-model/filterValues";
import { NycbsPharmaOrderModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import {
  getKeyFromEnumValuesForOrderStatus,
  OrderStatus,
} from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import {
  NycbsPharmaOrderKeys,
  NycbsPharmaOrderRequiredKeys,
  NycbsPharmaOrderRequiredKeysLabelForUI,
} from "../../../enums/nycbsPharmaOrder";
import { CmmOrderTableColumnKeys } from "../../../enums/tableColumnKeys";
import { filterData } from "../../../utils/filterData";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../utils/stringModifications";
import { findLabelFromGivenState } from "../../pharmaPaForm/utils/stateOptions";

export const CmmOrderTableHeader: TableHeader[] = [
  {
    label: "Patient Details",
    key: CmmOrderTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 12,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Service",
    key: CmmOrderTableColumnKeys.DATE_OF_SERVICE,
    order: 2,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Medication",
    key: CmmOrderTableColumnKeys.MEDICATION,
    order: 3,
    width: 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Provider Details",
    key: CmmOrderTableColumnKeys.PROVIDER_DETAILS,
    order: 4,
    width: 11,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Form Name",
    key: CmmOrderTableColumnKeys.FORM_NAME,
    order: 5,
    width: 16,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Key",
    key: CmmOrderTableColumnKeys.CMM_KEY,
    order: 7,
    width: 9,
    sortable: false,
    type: TableCellType.COPY_DATA,
  },
  {
    label: "Missing Data",
    key: CmmOrderTableColumnKeys.NO_DATA_FIELDS,
    order: 8,
    width: 9,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Status",
    key: CmmOrderTableColumnKeys.STATUS,
    order: 9,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "",
    key: CmmOrderTableColumnKeys.ACTION,
    order: 10,
    width: 8,
    sortable: false,
    type: TableCellType.BUTTON_LINK,
  },
  {
    label: "",
    key: CmmOrderTableColumnKeys.DELETE_ICON,
    order: 11,
    width: 4,
    sortable: false,
    type: TableCellType.DELETE_ICON,
  },
];

export const generateTableDataForCmmOrdersTable = (
  data: NycbsPharmaOrderModel[],
): CmmOrdersTableDataModel[] => {
  return data.map((item) => {
    const noDataFields: string[] = generateFieldNamesWhichHaveNoValue(item);

    const patientDetails: TableCellMultiline = {
      mainText: capitalizeString(
        generateFullName(
          item?.[NycbsPharmaOrderKeys.PatientFirstName] ?? "",
          "",
          item?.[NycbsPharmaOrderKeys.PatientLastName] ?? "",
        ),
      ),
      secondaryText: `MRN : ${item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "--"}`,
    };
    const dateOfService: string = item?.[NycbsPharmaOrderKeys.CreatedAt]
      ? moment(item?.[NycbsPharmaOrderKeys.CreatedAt]).format("MM/DD/YYYY")
      : "--";
    const medication: string = item?.[NycbsPharmaOrderKeys.DrugName] ?? "--";
    const providerDetails: string = capitalizeString(
      generateFullName(
        item?.[NycbsPharmaOrderKeys.ProviderFirstName] ?? "",
        "",
        item?.[NycbsPharmaOrderKeys.ProviderLastName] ?? "",
      ),
    );
    const dateOfBirth: string = item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--";

    const insuranceState: string =
      findLabelFromGivenState(
        item?.[NycbsPharmaOrderKeys.PatientInsuranceState] ?? "",
      ) ?? "--";
    const button: string = "Start Filing";

    const id = item?.[NycbsPharmaOrderKeys.Identifier] ?? "";

    const status = getKeyFromEnumValuesForOrderStatus(
      item?.[NycbsPharmaOrderKeys.Status] ?? "",
    );

    const badgeText = {
      text: status ? OrderStatus[status] : "No Status",
      color: BadgeTextColor[status ? OrderStatus[status] : "No Status"],
      bgColor: BadgeColor[status ? OrderStatus[status] : "No Status"],
    };

    const noDataFieldsBadge: TableCellWithBadge = {
      text: noDataFields.length > 0 ? `Yes (${noDataFields.length})` : "No",
      color:
        noDataFields.length > 0
          ? BadgeTextColor["Error"]
          : BadgeTextColor["Approved"],
      bgColor:
        noDataFields.length > 0 ? BadgeColor["Error"] : BadgeColor["Approved"],
      displayText:
        noDataFields.length > 0 ? noDataFields.join(", ") : "No Missing Fields",
    };

    const cmmKey = item?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "";
    const formName = item?.[NycbsPharmaOrderKeys.FormName] ?? "";

    return {
      patientDetails,
      dateOfService,
      medication,
      providerDetails,
      dateOfBirth,
      insuranceState,
      button,
      status: badgeText,
      id,
      cmmKey,
      formName,
      cmmOrderDeleteIcon: id,
      patientId: item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "",
      rowData: JSON.stringify(item),
      noDataFields: noDataFieldsBadge,
    };
  });
};

export const generateFilterDataForCmmOrdersTable = (
  data: NycbsPharmaOrderModel[],
  filterValues: FilterValues,
) => {
  const filteredData = filterData(data, filterValues);
  return filteredData;
};

export const generateFieldNamesWhichHaveNoValue = (
  data: NycbsPharmaOrderModel,
) => {
  const fieldNames: string[] = [];
  for (const key in NycbsPharmaOrderRequiredKeys) {
    const valueMapping = NycbsPharmaOrderRequiredKeys[key];
    if (valueMapping === NycbsPharmaOrderKeys.PrimaryDiagnoses) {
      const dataRequiredForPrimaryDiagnosis =
        data?.[NycbsPharmaOrderKeys.IsDiagnosisCodeAvailableOnForm];
      if (
        dataRequiredForPrimaryDiagnosis &&
        (data[valueMapping] === undefined ||
          data?.[valueMapping] === "" ||
          data?.[valueMapping] === null)
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (valueMapping === NycbsPharmaOrderKeys.FormName) {
      const formName = data?.[valueMapping];
      if (
        formName === undefined ||
        formName === null ||
        formName === "" ||
        formName?.toLowerCase().includes("no forms available")
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (
      data?.[valueMapping] === undefined ||
      data?.[valueMapping] === null ||
      data?.[valueMapping] === "" ||
      data?.[valueMapping]?.toString()?.trim() === "0"
    ) {
      fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
    }
  }
  return fieldNames;
};
